import React, { useState, useEffect, useCallback } from "react";
import $ from "jquery";
import { toast } from "react-toastify";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw, ContentState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import RiskRuleForProcedure from "./RiskRuleForProcedure";
import { isRtlLanguage, textDirection } from "./Utils";

const ALL_LANGUAGES = [
  { code: "EN", displayName: "English", englishName: "English" },
  { code: "AR", displayName: "العربية", englishName: "Arabic" },
  { code: "FR", displayName: "Français", englishName: "French" },
  { code: "DE", displayName: "Deutsch", englishName: "German" },
  { code: "ES", displayName: "Español", englishName: "Spanish" },
];

// Risk rule predefined messages are stored as codes. Show the sentence they stand for,
// not the raw code. Keep in sync with PatientMessageTranslationProvider on the server
// and with the predefined message options in RiskRuleForProcedure.js.
const PREDEFINED_PATIENT_MESSAGES = {
  ER: "Proceed to the Emergency Room immediately",
  DOCTOR: "Call your doctor immediately",
  BUSINESS_HOURS: "Call your doctor during the next available business hours",
};

function resolvePreDefinedMessage(code) {
  if (!code) return "";
  return PREDEFINED_PATIENT_MESSAGES[code] || code;
}

// Describe a risk rule component exactly as the Risk Rules configuration page does, by
// reusing its formatter (it only reads the rule and getComparisonSymbol).
function riskRuleCondition(rule, riskCategory) {
  return RiskRuleForProcedure.prototype.getRuleString.call(RiskRuleForProcedure.prototype, rule, riskCategory);
}

function authHeaders(extra) {
  return Object.assign(
    { Authorization: "Bearer " + (localStorage.getItem("access_token") || "") },
    extra || {}
  );
}

function htmlToEditorState(html) {
  if (!html) return EditorState.createEmpty();
  const block = htmlToDraft(html || "");
  const { contentBlocks, entityMap } = block;
  const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
  return EditorState.createWithContent(contentState);
}

function editorStateToHtml(editorState) {
  if (!editorState) return "";
  return draftToHtml(convertToRaw(editorState.getCurrentContent()));
}

// Translatable Item Component for Content Translation
function TranslatableItem({ entityType, entityId, label, originalContent, selectedLanguage, onSave, savingState, translationValue, onTranslationChange, isRich, editorState, onEditorStateChange, fieldType, showApproveButton, onApprove, isApproveDisabled, procedureId: propProcedureId, autoTranslateIfMissing }) {
  // Get procedureId from localStorage (primary source) or from props (fallback)
  const procedureId = localStorage.getItem("procedureId") || propProcedureId;

  const [translationData, setTranslationData] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isAutoTranslating, setIsAutoTranslating] = useState(false);
  const [translationExists, setTranslationExists] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const prevSavingRef = React.useRef(null);

  const cacheKey = fieldType ? `${entityType}-${entityId}-${fieldType}` : `${entityType}-${entityId}`;
  const isSaving = savingState && savingState[cacheKey];

  useEffect(() => {
    setIsLoaded(false);
  }, [selectedLanguage]);

  useEffect(() => {
    if (prevSavingRef.current === true && isSaving === false) {
      setIsLoaded(false);
      setIsEditMode(false);
    }
    prevSavingRef.current = isSaving;
  }, [isSaving]);

  useEffect(() => {
    const loadTranslation = async () => {
      if (!isLoaded) {
        try {
          const response = await $.ajax({
            url: `/my-jeeva-api/translation-edit/${entityType}/${entityId}?language=${selectedLanguage.toLowerCase()}&procedureId=${procedureId}`,
            type: "GET",
            dataType: "json",
            headers: authHeaders(),
          });

          const data = response && response.result ? response.result : {};
          const fields = data.fields || {};

          let content = "";
          if (fieldType) {
            content = fields[fieldType] || "";
          } else {
            content = fields.title || fields.question || fields.content || fields.message || fields.caption || fields.translatedMessage || fields.label || fields.textContent || fields.description || fields.translatedTitle || fields.translatedQuestion || fields.translatedContent || fields.translatedCaption || "";
          }
          const hasExistingTranslation = content && content.trim() !== "";

          console.log(`[DB FETCH] ${entityType}/${entityId} (${selectedLanguage}):`, { hasExistingTranslation, content: content.substring(0, 50) });

          setTranslationData(data);
          setTranslationExists(hasExistingTranslation);

          if (hasExistingTranslation) {
            console.log(`[DB HIT] Using existing translation from DB`);
            if (isRich) {
              onEditorStateChange(htmlToEditorState(content));
            } else {
              onTranslationChange(entityType, entityId, content, fieldType);
            }
            setIsLoaded(true);
          } else if (autoTranslateIfMissing && response && response.success && (originalContent || "").replace(/<[^>]*>/g, "").trim()) {
            // Nothing stored for this field yet (e.g. library content added after the language was
            // enabled): translate the English here and save it, so the item can be reviewed and approved.
            // Saved directly rather than through onSave, which reloads the whole tree for every item.
            setIsAutoTranslating(true);
            try {
              const autoResponse = await $.ajax({
                url: `/my-jeeva-api/translation-edit/auto-translate`,
                type: "POST",
                dataType: "json",
                headers: authHeaders(),
                // Sent in the body, not the query string - library HTML can be too long for a URL
                data: { language: selectedLanguage.toLowerCase(), sourceText: originalContent },
              });
              const translated = autoResponse && autoResponse.success && autoResponse.result ? autoResponse.result.translatedText : "";

              if (translated && translated.trim()) {
                const saveResponse = await $.ajax({
                  url: `/my-jeeva-api/translation-edit/${entityType}/${entityId}?language=${selectedLanguage.toLowerCase()}&procedureId=${procedureId}`,
                  type: "PUT",
                  dataType: "json",
                  contentType: "application/json",
                  headers: authHeaders(),
                  data: JSON.stringify({ [fieldType || "content"]: translated }),
                });

                if (saveResponse && saveResponse.success) {
                  console.log(`[AUTO-TRANSLATE] Translated and saved ${entityType}/${entityId} ${fieldType || ""} (${selectedLanguage})`);
                  setTranslationExists(true);
                  if (isRich) {
                    onEditorStateChange(htmlToEditorState(translated));
                  } else {
                    onTranslationChange(entityType, entityId, translated, fieldType);
                  }
                }
              }
            } catch (err) {
              console.error(`[AUTO-TRANSLATE] Failed for ${entityType}/${entityId}:`, err);
            } finally {
              setIsAutoTranslating(false);
              setIsLoaded(true);
            }
          } else {
            console.log(`[SKIP] No DB translation - backend auto-translation in progress`);
            setIsLoaded(true);
          }
        } catch (err) {
          console.error(`Error fetching translation:`, err);
          onTranslationChange(entityType, entityId, "");
          setIsLoaded(true);
        }
      }
    };

    loadTranslation();
  }, [entityType, entityId, selectedLanguage, isLoaded, originalContent, fieldType]);

  const handleSave = () => {
    const getFieldName = () => {
      if (fieldType) {
        return fieldType;
      }
      if (label.includes("Title")) return "title";
      if (label.includes("Message")) return "message";
      if (label.includes("Question")) return "question";
      if (label.includes("Caption")) return "caption";
      if (label.includes("Label")) return "label";
      if (label.includes("Options")) return "options";
      return "content";
    };

    if (isRich) {
      const html = editorStateToHtml(editorState);
      if (html && html.trim() && html !== "<p></p>") {
        let payload = {};
        const fieldName = getFieldName();
        payload[fieldName] = html;
        onSave(entityType, entityId, payload, fieldType);
      }
    } else {
      if (translationValue && translationValue.trim()) {
        let payload = {};
        const fieldName = getFieldName();
        payload[fieldName] = translationValue;
        onSave(entityType, entityId, payload, fieldType);
      }
    }
  };

  if (isLoaded && !translationExists) {
    return null;
  }

  return (
    <div style={{ padding: "12px 16px", background: "#fff", marginBottom: "8px", border: "1px solid #e0e0e0", borderRadius: "0" }}>
      <div style={{ fontSize: "12px", fontWeight: "600", color: "#333", marginBottom: "8px" }}>
        {label}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", alignItems: "start" }}>
        <div>
          <div style={{ fontSize: "11px", fontWeight: "600", color: "#999", marginBottom: "4px" }}>Original (English)</div>
          {isRich ? (
            <div style={{ background: "#f5f5f5", padding: "8px", border: "1px solid #e0e0e0", borderRadius: "0", minHeight: "80px", fontSize: "12px", maxHeight: "300px", overflowY: "auto" }}>
              <div dangerouslySetInnerHTML={{ __html: originalContent || "" }} />
            </div>
          ) : (
            <textarea
              value={originalContent || ""}
              readOnly
              style={{ background: "#f5f5f5", padding: "8px", borderRadius: "0", border: "1px solid #e0e0e0", fontSize: "12px", width: "100%", minHeight: "80px", maxHeight: "300px", fontFamily: "inherit", resize: "none", overflowY: "auto" }}
            />
          )}
        </div>

        <div>
          <div style={{ fontSize: "11px", fontWeight: "600", color: "#999", marginBottom: "4px" }}>({selectedLanguage})</div>
          {isRich ? (
            <div style={{ border: "1px solid #e0e0e0", borderRadius: "0", background: !isEditMode && translationExists ? "#f5f5f5" : "#fff", minHeight: "80px", maxHeight: "300px", overflowY: "auto" }}>
              <Editor
                editorState={editorState}
                onEditorStateChange={onEditorStateChange}
                toolbar={isEditMode || !translationExists ? { options: ["inline", "blockType", "list", "link", "emoji"] } : false}
                readOnly={!isEditMode && translationExists && !isAutoTranslating}
                wrapperClassName="editor-wrapper"
                editorClassName="editor-class"
                toolbarClassName="toolbar-class"
                textAlignment={isRtlLanguage(selectedLanguage) ? "right" : "left"}
                editorStyle={{ direction: textDirection(selectedLanguage) }}
              />
            </div>
          ) : (
            <textarea
              dir={textDirection(selectedLanguage)}
              value={translationValue || ""}
              onChange={(e) => onTranslationChange(entityType, entityId, e.target.value, fieldType)}
              placeholder={isAutoTranslating ? "Translating..." : "No translation yet"}
              readOnly={!isEditMode && translationExists}
              disabled={isAutoTranslating}
              style={{ background: !isEditMode && translationExists ? "#f5f5f5" : "#fff", padding: "8px", borderRadius: "0", border: "1px solid #e0e0e0", fontSize: "12px", width: "100%", minHeight: "80px", maxHeight: "300px", fontFamily: "inherit", resize: "none", overflowY: "auto", opacity: !isEditMode && translationExists ? 0.7 : 1, cursor: !isEditMode && translationExists ? "not-allowed" : "text" }}
            />
          )}
          <div style={{ display: "flex", gap: "8px", marginTop: "8px", alignItems: "center" }}>
            {translationExists && !isEditMode && (
              <button
                style={{ background: "#282a65", border: "none", padding: "6px 12px", borderRadius: "3px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: 1 }}
                onClick={() => setIsEditMode(true)}
                disabled={isAutoTranslating}
                title="Edit Translation"
              >
                <img
                  className="img-responsive"
                  style={{
                    filter: "brightness(0) invert(1)",
                    height: "16px",
                    width: "16px"
                  }}
                  src="assets/images/edit.svg"
                  alt="edit"
                />
              </button>
            )}
            {!isEditMode && showApproveButton && onApprove && (
              <button
                style={{
                  padding: "6px 12px",
                  borderRadius: "3px",
                  border: "1px solid #ff9800",
                  fontSize: "12px",
                  fontWeight: "600",
                  cursor: "pointer",
                  background: isApproveDisabled ? "#4caf50" : "#ff9800",
                  color: "#fff",
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={onApprove}
                disabled={isApproveDisabled}
                onMouseEnter={(e) => !isApproveDisabled && (e.target.style.background = "#e68900")}
                onMouseLeave={(e) => !isApproveDisabled && (e.target.style.background = "#ff9800")}
                title={isApproveDisabled ? "Approved" : "Approve Translation"}
              >
                {isApproveDisabled ? "✓ Approved" : "Approve"}
              </button>
            )}
            {isEditMode && (
              <React.Fragment>
                <button
                  style={{
                    padding: "6px 12px",
                    borderRadius: "3px",
                    border: "1px solid #4caf50",
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                    background: "#4caf50",
                    color: "#fff",
                  }}
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save"}
                </button>
                <button
                  style={{
                    padding: "6px 12px",
                    borderRadius: "3px",
                    border: "1px solid #999",
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                    background: "#fff",
                    color: "#333",
                  }}
                  onClick={() => {
                    setIsEditMode(false);
                    setIsLoaded(false);
                  }}
                >
                  Cancel
                </button>
              </React.Fragment>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TranslationApprovalsTab({ procedureId: propProcedureId, activeTab }) {
  // Get procedureId from localStorage (primary source) or from props (fallback)
  const procedureId = localStorage.getItem("procedureId") || propProcedureId;

  // Get user details from localStorage
  const userDetailsStr = localStorage.getItem("user_details");
  let userEmail = "";
  let userId = "";

  if (userDetailsStr) {
    try {
      const userDetails = JSON.parse(userDetailsStr);
      userEmail = userDetails.userEmailId || userDetails.emailId || "";
      userId = userDetails.userId || "";
    } catch (e) {
      console.error("Error parsing user details:", e);
    }
  }

  // State management for translation approvals
  const [availableLanguages, setAvailableLanguages] = useState(null); // null = loading, [] = no languages, [...] = languages exist
  const [selectedTranslateLanguage, setSelectedTranslateLanguage] = useState("");
  const [surveys, setSurveys] = useState([]);
  const [journals, setJournals] = useState([]);
  const [surveyReminders, setSurveyReminders] = useState({});
  const [journalReminders, setJournalReminders] = useState({});
  const [libraryContent, setLibraryContent] = useState([]);
  const [libraryContentId, setLibraryContentId] = useState(null);
  const [riskRules, setRiskRules] = useState([]);

  // Loading state for each section
  const [standaloneReminders, setStandaloneReminders] = useState([]);
  const [loadingState, setLoadingState] = useState({
    surveys: false,
    journals: false,
    library: false,
    riskRules: false,
    standaloneReminders: false,
  });

  // Translation state
  const [savingTranslations, setSavingTranslations] = useState({});
  const [translationValues, setTranslationValues] = useState({});
  const [editorStates, setEditorStates] = useState({});

  // Approval state
  const [approvalStatus, setApprovalStatus] = useState({});
  const [approvingItems, setApprovingItems] = useState({});
  const [modalApprovalStatus, setModalApprovalStatus] = useState({});

  // Navigation state
  const [expandedSections, setExpandedSections] = useState({});

  // Tab loading state
  const [isLoadingTab, setIsLoadingTab] = useState(false);
  const [noLanguagesConfigured, setNoLanguagesConfigured] = useState(false);
  const [loadLanguagesFailed, setLoadLanguagesFailed] = useState(false);

  // Load languages first when tab becomes active
  useEffect(() => {
    if (procedureId && activeTab === "content") {
      setAvailableLanguages(null); // Set to null to show loading message
      setLoadLanguagesFailed(false);
      loadLanguages();
    }
  }, [procedureId, activeTab]);

  // Reload approval statuses when language selection changes (after initial load)
  useEffect(() => {
    if (selectedTranslateLanguage && availableLanguages !== null && availableLanguages.length > 0) {
      console.log(`[LANGUAGE-CHANGE-EFFECT] Language changed to: ${selectedTranslateLanguage}, reloading approval statuses`);
      loadProcedureApprovalStatuses();
    }
  }, [selectedTranslateLanguage]);

  // Fetch approval status when Library Content section is expanded
  useEffect(() => {
    if (expandedSections["library"] && selectedTranslateLanguage && libraryContentId) {
      console.log("[LIBRARY-EXPAND] Library section expanded, fetching approval status");
      loadApprovalStatus(libraryContentId, "LIBRARY");
    }
  }, [expandedSections["library"], selectedTranslateLanguage, libraryContent]);

  const loadLanguages = async () => {
    if (!procedureId) {
      console.warn("No procedureId provided");
      return;
    }

    try {
      const response = await $.ajax({
        url: `/my-jeeva-api/procedure/${procedureId}/languages`,
        type: "GET",
        dataType: "json",
        headers: authHeaders(),
      });

      console.log("[LOAD-LANGUAGES] API Response:", response);

      const responseArray = response && response.result ? response.result : [];
      console.log("[LOAD-LANGUAGES] Languages from API:", responseArray);

      // Filter: Only enabled languages, exclude EN from configured count
      const enabledLanguages = responseArray
        .filter((lang) => lang.isEnabled === true || lang.isEnabled === "true")
        .map((lang) => {
          const code = lang.code.toUpperCase();
          const langObj = ALL_LANGUAGES.find((l) => l.code === code);
          return langObj || { code, displayName: lang.name, englishName: lang.englishName };
        });

      console.log("[LOAD-LANGUAGES] Enabled languages:", enabledLanguages);

      // Only non-English languages count as configured. English is the source
      // language, so a treatment with nothing enabled - or with EN alone - has
      // nothing to translate and gets the "no languages" message.
      const nonEnglishLanguages = enabledLanguages.filter((lang) => lang.code !== "EN");
      console.log("[LOAD-LANGUAGES] Non-English languages:", nonEnglishLanguages);

      if (nonEnglishLanguages.length === 0) {
        console.warn("[LOAD-LANGUAGES] No translation languages enabled for this procedure");
        setLoadLanguagesFailed(false);
        setAvailableLanguages([]);
        setIsLoadingTab(false);
        return;
      }

      const defaultLang = nonEnglishLanguages[0].code;
      console.log("[LOAD-LANGUAGES] Default language:", defaultLang);

      // Load modal approval data in parallel with setting languages
      await loadProcedureApprovalStatuses(defaultLang);

      // Set languages and language selection together
      setLoadLanguagesFailed(false);
      setAvailableLanguages(nonEnglishLanguages);
      setSelectedTranslateLanguage(defaultLang);
      setIsLoadingTab(false);
    } catch (err) {
      console.error("Error loading languages:", err);
      // Leaving availableLanguages as null would strand the tab on the loading
      // message, so surface the failure instead of pretending no languages exist.
      setLoadLanguagesFailed(true);
      setAvailableLanguages([]);
      setIsLoadingTab(false);
    }
  };

  const loadProcedureApprovalStatuses = async (languageOverride = null) => {
    if (!procedureId) {
      console.warn("No procedureId provided");
      return;
    }

    const langCode = languageOverride || selectedTranslateLanguage;
    if (!langCode) {
      console.warn("[LOAD-PROCEDURE-STATUS] No language code available");
      return;
    }

    try {
      console.log(`[LOAD-PROCEDURE-STATUS] Fetching approval statuses for procedure: ${procedureId}, language: ${langCode}`);

      const response = await $.ajax({
        url: `/my-jeeva-api/translation-approval/procedure/${procedureId}/approval-status`,
        type: "GET",
        dataType: "json",
        headers: authHeaders(),
        data: {
          languageCode: langCode
        }
      });

      if (response && response.success && response.result) {
        console.log(`[LOAD-PROCEDURE-STATUS] Received approval statuses:`, response.result);

        // Store the new simplified response format with overallStatus and categoryStatuses
        setModalApprovalStatus(response.result);
        console.log(`[LOAD-PROCEDURE-STATUS] Modal approval status updated successfully:`, response.result);
      }
    } catch (err) {
      console.error(`[LOAD-PROCEDURE-STATUS] Error fetching approval statuses:`, err);
    }
  };

  const saveTranslation = async (entityType, entityId, payload, fieldType) => {
    const cacheKey = fieldType ? `${entityType}-${entityId}-${fieldType}` : `${entityType}-${entityId}`;
    try {
      setSavingTranslations((prev) => ({ ...prev, [cacheKey]: true }));
      await $.ajax({
        url: `/my-jeeva-api/translation-edit/${entityType}/${entityId}?language=${selectedTranslateLanguage.toLowerCase()}&procedureId=${procedureId}`,
        type: "PUT",
        dataType: "json",
        contentType: "application/json",
        headers: authHeaders(),
        data: JSON.stringify(payload),
      });
      toast.dismiss();
      toast(<b><img src="assets/images/done.svg" style={{ paddingRight: '8px', height: '20px', width: '20px' }} content="" />&nbsp;&nbsp;Translation updated successfully</b>, {
        position: "top-center",
      });

      const openSection = Object.keys(expandedSections).find((key) => expandedSections[key]);
      if (openSection === "surveys") await loadSurveys();
      else if (openSection === "journals") await loadJournals();
      else if (openSection === "library") await loadLibraryContent();
      else if (openSection === "riskRules") await loadRiskRulesData();
      else if (openSection === "standaloneReminders") await loadStandaloneReminders();

      await loadApprovalStatus();
    } catch (err) {
      console.error("Error saving translation:", err);
      toast.error("Failed to save translation");
    } finally {
      setSavingTranslations((prev) => ({ ...prev, [cacheKey]: false }));
    }
  };

  const loadApprovalStatus = async (contentId, approvalType) => {
    console.log(`[LOAD-STATUS] contentId=${contentId}, approvalType=${approvalType}, language=${selectedTranslateLanguage}`);
    try {
      const response = await $.ajax({
        url: `/my-jeeva-api/translation-approval/status`,
        type: "GET",
        dataType: "json",
        headers: authHeaders(),
        data: {
          contentId: contentId,
          approvalType: approvalType,
          languageCode: selectedTranslateLanguage,
        },
      });

      if (response && response.success && response.result) {
        const approvalKey = `${approvalType}-${contentId}-${selectedTranslateLanguage}`;
        // Map "approved" field to "isFullyApproved" if not present
        if (!response.result.isFullyApproved && response.result.approved !== undefined) {
          response.result.isFullyApproved = response.result.approved;
        }
        console.log(`[LOAD-STATUS] Success: ${approvalKey}`, response.result);
        setApprovalStatus((prev) => ({
          ...prev,
          [approvalKey]: response.result,
        }));
      }
    } catch (err) {
      console.error(`[LOAD-STATUS] Error:`, err);
    }
  };

  const approveSingleItem = async (contentId, itemId, itemType, suppressToast = false, checkComplete = true) => {
    console.log(`[APPROVE-ITEM] Starting approval: contentId=${contentId}, itemId=${itemId}, itemType=${itemType}`);
    try {
      let contentType = "SURVEY";
      if (itemType.includes("JOURNAL")) {
        contentType = "JOURNAL";
      } else if (itemType.includes("LIBRARY")) {
        contentType = "LIBRARY";
      } else if (itemType === "PATIENTMESSAGE") {
        contentType = "PATIENTMESSAGE";
      }

      const response = await $.ajax({
        url: `/my-jeeva-api/translation-approval/approve-item`,
        type: "POST",
        dataType: "json",
        contentType: "application/json",
        headers: authHeaders(),
        data: JSON.stringify({
          contentId: contentId,
          itemId: itemId,
          contentType: contentType,
          itemType: itemType,
          languageCode: selectedTranslateLanguage,
          approvedByEmail: userEmail,
          approvedByUserId: userId,
        }),
      });

      if (response && response.success) {
        console.log(`[APPROVE-ITEM] Item ${itemId} approved successfully`, response);

        // Check if itemId is in the appropriate array (approvedItemIds for items, approvedReminderIds for reminders)
        const isItemApproved = response.result && (
          (itemType.includes("REMINDER") && response.result.approvedReminderIds && response.result.approvedReminderIds.includes(itemId)) ||
          (!itemType.includes("REMINDER") && response.result.approvedItemIds && response.result.approvedItemIds.includes(itemId))
        );

        if (isItemApproved) {
          // Update local approval status with approved items/reminders, but NOT isFullyApproved
          // isFullyApproved should only be set when ALL items are approved
          const approvalKey = `${contentType}-${contentId}-${selectedTranslateLanguage}`;
          setApprovalStatus((prev) => ({
            ...prev,
            [approvalKey]: {
              ...prev[approvalKey],
              approvedItemIds: response.result.approvedItemIds || [],
              approvedReminderIds: response.result.approvedReminderIds || [],
              // Keep isFullyApproved unchanged - only mark as true via /mark-fully-approved
            }
          }));

          console.log(`[APPROVE-ITEM] Updated approval status for ${approvalKey}. Items: ${response.result.approvedItemIds.length}, Reminders: ${response.result.approvedReminderIds.length}`);

          if (checkComplete) {
            await checkAndMarkFullyApproved(contentId, contentType, response.result);
          }
        }

        if (!suppressToast) {
          toast.dismiss();
          toast(<b><img src="assets/images/done.svg" style={{ paddingRight: '8px', height: '20px', width: '20px' }} content="" />&nbsp;&nbsp;Translations approved successfully</b>, {
            position: "top-center",
          });
        }
      }
    } catch (err) {
      console.error("[APPROVE-ITEM] Error:", err);
      toast.error("Failed to approve item");
    }
  };

  // Section and folder ids of the whole library tree, at every depth.
  const getAllLibraryIds = (items) => {
    let allIds = [];
    for (const item of items) {
      if (item.isSection || item.isFolder) {
        allIds.push(item.id);
      }
      if (item.children && item.children.length > 0) {
        allIds = allIds.concat(getAllLibraryIds(item.children));
      }
    }
    return allIds;
  };

  // Once every item of a survey / journal / library has been approved one by one, mark the entity
  // fully approved, exactly as its "Approve All" button would. The expected ids mirror what those
  // buttons send: the title plus every question for surveys and journals, plus their reminders.
  const checkAndMarkFullyApproved = async (contentId, contentType, approval) => {
    try {
      let expectedItemIds = [];
      let expectedReminderIds = [];

      if (contentType === "SURVEY") {
        const survey = surveys.find((s) => s.id === contentId);
        if (!survey) return;
        expectedItemIds = [survey.id, ...(survey.items || []).map((item) => item.id)];
        expectedReminderIds = (surveyReminders[contentId] || []).map((reminder) => reminder.id);
      } else if (contentType === "JOURNAL") {
        const journal = journals.find((j) => j.id === contentId);
        if (!journal) return;
        expectedItemIds = [journal.id, ...(journal.items || []).map((item) => item.id)];
        expectedReminderIds = (journalReminders[contentId] || []).map((reminder) => reminder.id);
      } else if (contentType === "LIBRARY") {
        if (contentId !== libraryContentId) return;
        expectedItemIds = getAllLibraryIds(libraryContent);
      } else {
        return;
      }

      const approvedItemIds = approval.approvedItemIds || [];
      const approvedReminderIds = approval.approvedReminderIds || [];
      const allApproved =
        expectedItemIds.length > 0 &&
        expectedItemIds.every((id) => approvedItemIds.includes(id)) &&
        expectedReminderIds.every((id) => approvedReminderIds.includes(id));
      if (!allApproved) return;

      console.log(`[AUTO-APPROVE] Every item of ${contentType} ${contentId} approved. Marking as fully approved.`);
      await $.ajax({
        url: `/my-jeeva-api/translation-approval/mark-fully-approved`,
        type: "POST",
        dataType: "json",
        headers: authHeaders(),
        data: {
          contentId: contentId,
          approvalType: contentType,
          languageCode: selectedTranslateLanguage,
        },
      });
      await loadApprovalStatus(contentId, contentType);
      await loadProcedureApprovalStatuses();
    } catch (err) {
      console.error("[AUTO-APPROVE] Error:", err);
    }
  };

  const approveJournalItems = async (journalId) => {
    const journal = journals.find((j) => j.id === journalId);
    if (!journal) {
      toast.error("Journal not found");
      return;
    }

    const approvalKey = `JOURNAL-${journalId}-${selectedTranslateLanguage}`;
    console.log(`[APPROVE-JOURNAL] Starting bulk approval for journal: ${journalId}`);

    try {
      setApprovingItems((prev) => ({ ...prev, [approvalKey]: true }));

      // Add Journal Title ID first, then add all question IDs
      const itemIds = [journalId];  // Journal Title ID
      if (journal.items && journal.items.length > 0) {
        itemIds.push(...journal.items.map((item) => item.id));  // Question IDs
      }

      const reminderIds =
        journalReminders[journalId] && journalReminders[journalId].length > 0
          ? journalReminders[journalId].map((reminder) => reminder.id)
          : [];

      console.log(`[APPROVE-JOURNAL] Items to approve:`, {
        journalId,
        itemCount: itemIds.length,
        reminderCount: reminderIds.length,
      });

      let approvedCount = 0;
      for (const itemId of itemIds) {
        try {
          const response = await $.ajax({
            url: `/my-jeeva-api/translation-approval/approve-item`,
            type: "POST",
            dataType: "json",
            contentType: "application/json",
            headers: authHeaders(),
            data: JSON.stringify({
              contentId: journalId,
              itemId: itemId,
              contentType: "JOURNAL",
              itemType: "JOURNALITEM",
              languageCode: selectedTranslateLanguage,
              approvedByEmail: userEmail,
              approvedByUserId: userId,
            }),
          });

          if (response && response.success) {
            approvedCount++;
          }
        } catch (err) {
          console.error(`[APPROVE-JOURNAL] Error approving item ${itemId}:`, err);
        }
      }

      for (const reminderId of reminderIds) {
        try {
          const response = await $.ajax({
            url: `/my-jeeva-api/translation-approval/approve-item`,
            type: "POST",
            dataType: "json",
            contentType: "application/json",
            headers: authHeaders(),
            data: JSON.stringify({
              contentId: journalId,
              itemId: reminderId,
              contentType: "JOURNAL",
              itemType: "JOURNALREMINDER",
              languageCode: selectedTranslateLanguage,
              approvedByEmail: userEmail,
              approvedByUserId: userId,
            }),
          });

          if (response && response.success) {
            approvedCount++;
          }
        } catch (err) {
          console.error(`[APPROVE-JOURNAL] Error approving reminder ${reminderId}:`, err);
        }
      }

      try {
        await $.ajax({
          url: `/my-jeeva-api/translation-approval/mark-fully-approved`,
          type: "POST",
          dataType: "json",
          headers: authHeaders(),
          data: {
            contentId: journalId,
            approvalType: "JOURNAL",
            languageCode: selectedTranslateLanguage,
          },
        });
      } catch (err) {
        console.error(`[APPROVE-JOURNAL] Error marking as fully approved:`, err);
      }

      await loadApprovalStatus(journalId, "JOURNAL");
      await loadProcedureApprovalStatuses();
      toast.dismiss();
      toast(<b><img src="assets/images/done.svg" style={{ paddingRight: '8px', height: '20px', width: '20px' }} content="" />&nbsp;&nbsp;Translations approved successfully</b>, {
        position: "top-center",
      });
    } catch (err) {
      console.error("[APPROVE-JOURNAL] Error:", err);
      toast.error("Failed to approve journal");
    } finally {
      setApprovingItems((prev) => ({ ...prev, [approvalKey]: false }));
    }
  };

  const approveSurveyItems = async (surveyId) => {
    const survey = surveys.find((s) => s.id === surveyId);
    if (!survey) {
      toast.error("Survey not found");
      return;
    }

    const approvalKey = `SURVEY-${surveyId}-${selectedTranslateLanguage}`;
    console.log(`[APPROVE-SURVEY] Starting bulk approval for survey: ${surveyId}`);

    try {
      setApprovingItems((prev) => ({ ...prev, [approvalKey]: true }));

      // Add Survey Title ID first, then add all item IDs
      const itemIds = [surveyId];  // Survey Title ID
      if (survey.items && survey.items.length > 0) {
        itemIds.push(...survey.items.map((item) => item.id));  // Item IDs
      }

      const reminderIds =
        surveyReminders[surveyId] && surveyReminders[surveyId].length > 0
          ? surveyReminders[surveyId].map((reminder) => reminder.id)
          : [];

      let approvedCount = 0;
      for (const itemId of itemIds) {
        try {
          const response = await $.ajax({
            url: `/my-jeeva-api/translation-approval/approve-item`,
            type: "POST",
            dataType: "json",
            contentType: "application/json",
            headers: authHeaders(),
            data: JSON.stringify({
              contentId: surveyId,
              itemId: itemId,
              contentType: "SURVEY",
              itemType: "SURVEYITEM",
              languageCode: selectedTranslateLanguage,
              approvedByEmail: userEmail,
              approvedByUserId: userId,
            }),
          });

          if (response && response.success) {
            approvedCount++;
          }
        } catch (err) {
          console.error(`[APPROVE-SURVEY] Error approving item ${itemId}:`, err);
        }
      }

      for (const reminderId of reminderIds) {
        try {
          const response = await $.ajax({
            url: `/my-jeeva-api/translation-approval/approve-item`,
            type: "POST",
            dataType: "json",
            contentType: "application/json",
            headers: authHeaders(),
            data: JSON.stringify({
              contentId: surveyId,
              itemId: reminderId,
              contentType: "SURVEY",
              itemType: "SURVEYREMINDER",
              languageCode: selectedTranslateLanguage,
              approvedByEmail: userEmail,
              approvedByUserId: userId,
            }),
          });

          if (response && response.success) {
            approvedCount++;
          }
        } catch (err) {
          console.error(`[APPROVE-SURVEY] Error approving reminder ${reminderId}:`, err);
        }
      }

      try {
        await $.ajax({
          url: `/my-jeeva-api/translation-approval/mark-fully-approved`,
          type: "POST",
          dataType: "json",
          headers: authHeaders(),
          data: {
            contentId: surveyId,
            approvalType: "SURVEY",
            languageCode: selectedTranslateLanguage,
          },
        });
      } catch (err) {
        console.error(`[APPROVE-SURVEY] Error marking as fully approved:`, err);
      }

      await loadApprovalStatus(surveyId, "SURVEY");
      await loadProcedureApprovalStatuses();
      toast.dismiss();
      toast(<b><img src="assets/images/done.svg" style={{ paddingRight: '8px', height: '20px', width: '20px' }} content="" />&nbsp;&nbsp;Translations approved successfully</b>, {
        position: "top-center",
      });
    } catch (err) {
      console.error("[APPROVE-SURVEY] Error:", err);
      toast.error("Failed to approve survey");
    } finally {
      setApprovingItems((prev) => ({ ...prev, [approvalKey]: false }));
    }
  };

  const approveSingleRiskRule = async (ruleId, messageId, ruleName) => {
    const approvalKey = `PATIENTMESSAGE-${messageId}-${selectedTranslateLanguage}`;
    console.log(`[APPROVE-SINGLE-RISKRULE] Starting approval for risk rule: ${ruleName}`);

    try {
      // Step 1: Approve the item
      const response = await $.ajax({
        url: `/my-jeeva-api/translation-approval/approve-item`,
        type: "POST",
        dataType: "json",
        contentType: "application/json",
        headers: authHeaders(),
        data: JSON.stringify({
          contentId: messageId,
          itemId: messageId,
          contentType: "PATIENTMESSAGE",
          itemType: "PATIENTMESSAGE",
          languageCode: selectedTranslateLanguage,
          approvedByEmail: userEmail,
          approvedByUserId: userId,
        }),
      });

      if (response && response.success) {
        console.log(`[APPROVE-SINGLE-RISKRULE] Item approved:`, response);

        // Step 2: Mark as fully approved
        try {
          await $.ajax({
            url: `/my-jeeva-api/translation-approval/mark-fully-approved`,
            type: "POST",
            dataType: "json",
            headers: authHeaders(),
            data: {
              contentId: messageId,
              approvalType: "PATIENTMESSAGE",
              languageCode: selectedTranslateLanguage,
            },
          });
        } catch (err) {
          console.error(`[APPROVE-SINGLE-RISKRULE] Error marking as fully approved:`, err);
        }

        // Step 3: Call /status API to get updated approval details
        try {
          const statusResponse = await $.ajax({
            url: `/my-jeeva-api/translation-approval/status`,
            type: "GET",
            dataType: "json",
            headers: authHeaders(),
            data: {
              contentId: messageId,
              approvalType: "PATIENTMESSAGE",
              languageCode: selectedTranslateLanguage,
            },
          });

          if (statusResponse && statusResponse.success && statusResponse.result) {
            console.log(`[APPROVE-SINGLE-RISKRULE] Status updated:`, statusResponse.result);
            // Update local approval status
            setApprovalStatus((prev) => ({
              ...prev,
              [approvalKey]: statusResponse.result,
            }));
          }
        } catch (err) {
          console.error(`[APPROVE-SINGLE-RISKRULE] Error fetching status:`, err);
        }

        // Step 4: Update modal approval statuses
        await loadProcedureApprovalStatuses();

        toast.dismiss();
        toast(<b><img src="assets/images/done.svg" style={{ paddingRight: '8px', height: '20px', width: '20px' }} content="" />&nbsp;&nbsp;Risk rule approved successfully</b>, {
          position: "top-center",
        });
      }
    } catch (err) {
      console.error("[APPROVE-SINGLE-RISKRULE] Error:", err);
      toast.error("Failed to approve risk rule");
    }
  };

  const approveRiskRules = async () => {
    if (riskRules.length === 0) {
      toast.error("No risk rules found");
      return;
    }

    const approvalKey = `PATIENTMESSAGE-ALL-${selectedTranslateLanguage}`;
    console.log(`[APPROVE-RISKRULES] Starting bulk approval for all risk rules`);

    try {
      setApprovingItems((prev) => ({ ...prev, [approvalKey]: true }));

      // Collect all patient message IDs from risk rules
      const messageIds = riskRules
        .filter((rule) => rule.patientMessageId)
        .map((rule) => rule.patientMessageId);

      console.log(`[APPROVE-RISKRULES] Messages to approve:`, { messageCount: messageIds.length });

      let approvedCount = 0;
      for (const messageId of messageIds) {
        try {
          const response = await $.ajax({
            url: `/my-jeeva-api/translation-approval/approve-item`,
            type: "POST",
            dataType: "json",
            contentType: "application/json",
            headers: authHeaders(),
            data: JSON.stringify({
              contentId: procedureId,
              itemId: messageId,
              contentType: "PATIENTMESSAGE",
              itemType: "PATIENTMESSAGE",
              languageCode: selectedTranslateLanguage,
              approvedByEmail: userEmail,
              approvedByUserId: userId,
            }),
          });

          if (response && response.success) {
            approvedCount++;
          }
        } catch (err) {
          console.error(`[APPROVE-RISKRULES] Error approving message ${messageId}:`, err);
        }
      }

      try {
        await $.ajax({
          url: `/my-jeeva-api/translation-approval/mark-fully-approved`,
          type: "POST",
          dataType: "json",
          headers: authHeaders(),
          data: {
            contentId: procedureId,
            approvalType: "PATIENTMESSAGE",
            languageCode: selectedTranslateLanguage,
          },
        });
      } catch (err) {
        console.error(`[APPROVE-RISKRULES] Error marking as fully approved:`, err);
      }

      await loadProcedureApprovalStatuses();
      toast.dismiss();
      toast(<b><img src="assets/images/done.svg" style={{ paddingRight: '8px', height: '20px', width: '20px' }} content="" />&nbsp;&nbsp;All risk rules approved successfully</b>, {
        position: "top-center",
      });
    } catch (err) {
      console.error("[APPROVE-RISKRULES] Error:", err);
      toast.error("Failed to approve risk rules");
    } finally {
      setApprovingItems((prev) => ({ ...prev, [approvalKey]: false }));
    }
  };

  const loadSurveys = useCallback(async () => {
    try {
      setLoadingState((prev) => ({ ...prev, surveys: true }));
      // Treatment-scoped, and every survey on it. fetch-survey returns only the caller's own
      // unpublished surveys, so a colleague's survey or a published one was missing from this
      // list while still being counted as pending by the approval status.
      const surveyResponse = await $.ajax({
        url: `/my-jeeva-api/fetch-treatment-survey-all?procedureId=${procedureId}`,
        type: "GET",
        headers: authHeaders(),
        dataType: "json",
      });

      const procedureSurveys = surveyResponse && surveyResponse.result ? surveyResponse.result : [];

      const surveysWithItems = await Promise.all(
        procedureSurveys.map(async (survey) => {
          try {
            const itemsResponse = await $.ajax({
              url: `/my-jeeva-api/fetch-survey-item?surveyId=${survey.id}&userEmail=${userEmail}`,
              type: "GET",
              headers: authHeaders(),
              dataType: "json",
            });
            return { ...survey, items: itemsResponse && itemsResponse.result ? itemsResponse.result : [] };
          } catch (err) {
            return { ...survey, items: [] };
          }
        })
      );

      setSurveys(surveysWithItems);

      surveysWithItems.forEach(async (survey) => {
        try {
          const reminderResponse = await $.ajax({
            url: `/my-jeeva-api/all-survey-Reminders?surveyId=${survey.id}`,
            type: "GET",
            headers: authHeaders(),
            dataType: "json",
          });
          setSurveyReminders((prev) => ({ ...prev, [survey.id]: reminderResponse && reminderResponse.result ? reminderResponse.result : [] }));
        } catch (err) {
          console.error("Error loading survey reminders:", err);
        }
      });
    } catch (err) {
      console.error("Error loading surveys:", err);
    } finally {
      setLoadingState((prev) => ({ ...prev, surveys: false }));
    }
  }, [userEmail, procedureId]);

  const loadJournals = useCallback(async () => {
    try {
      setLoadingState((prev) => ({ ...prev, journals: true }));
      // Treatment-scoped for every role, as for surveys. A provider used to get only the journals
      // they created, so an admin's journal on the treatment was missing from this list while
      // still being counted as pending by the approval status.
      const journalResponse = await $.ajax({
        url: `/my-jeeva-api/fetch-treatment-journal?procedureId=${procedureId}`,
        type: "GET",
        headers: authHeaders(),
        dataType: "json",
      });

      const allJournals = journalResponse && journalResponse.result ? journalResponse.result : [];

      const journalsWithItems = await Promise.all(
        allJournals.map(async (journal) => {
          try {
            const itemsResponse = await $.ajax({
              url: `/my-jeeva-api/fetch-journal-item?journalId=${journal.id}&userEmail=${userEmail}`,
              type: "GET",
              headers: authHeaders(),
              dataType: "json",
            });
            return { ...journal, items: itemsResponse && itemsResponse.result ? itemsResponse.result : [] };
          } catch (err) {
            return { ...journal, items: [] };
          }
        })
      );

      setJournals(journalsWithItems);

      journalsWithItems.forEach(async (journal) => {
        try {
          const reminderResponse = await $.ajax({
            url: `/my-jeeva-api/all-journal-Reminders?journalId=${journal.id}`,
            type: "GET",
            headers: authHeaders(),
            dataType: "json",
          });
          setJournalReminders((prev) => ({ ...prev, [journal.id]: reminderResponse && reminderResponse.result ? reminderResponse.result : [] }));
        } catch (err) {
          console.error("Error loading journal reminders:", err);
        }
      });
    } catch (err) {
      console.error("Error loading journals:", err);
    } finally {
      setLoadingState((prev) => ({ ...prev, journals: false }));
    }
  }, [userEmail, procedureId]);

  const loadLibraryContent = useCallback(async () => {
    try {
      setLoadingState((prev) => ({ ...prev, library: true }));
      let libraryId = null;

      // Sections then folders of one folder, each folder filled in with its own subtree, so content
      // at any depth is listed (and counted by getAllLibraryIds) rather than only the first two levels.
      const fetchFolderContent = async (parentFolderId, depth) => {
        const nodes = [];
        try {
          const response = await $.ajax({
            url: `/my-jeeva-api/library-module-admin/content`,
            type: "GET",
            headers: authHeaders(),
            dataType: "json",
            data: {
              scopeGroup: procedureId,
              scopeGroups: JSON.stringify([procedureId]),
              scope: "TREATMENT",
              parentFolderId: parentFolderId,
            },
          });

          if (response && response.result) {
            const result = response.result;
            const sections = result.documentSections || [];
            const folders = result.documentFolders || result.subFolderList || [];
            console.log(`[LIBRARY-LOAD] Folder ${parentFolderId}: ${sections.length} sections and ${folders.length} folders`);

            // The library's id, taken from the first item found (result.contentId is the scope group)
            if (!libraryId) {
              const first = sections[0] || folders[0];
              if (first && first.contentId) libraryId = first.contentId;
            }

            for (const section of sections) {
              nodes.push({ ...section, isSection: true, depth, children: [] });
            }
            for (const folder of folders) {
              nodes.push({
                ...folder,
                isFolder: true,
                depth,
                label: folder.label || folder.name,
                children: await fetchFolderContent(folder.id, depth + 1),
              });
            }
          }
        } catch (err) {
          console.error(`Error loading content for folder ${parentFolderId}:`, err);
        }
        return nodes;
      };

      const rootFolderId = "00000000-0000-0000-0000-000000000000";
      const hierarchyContent = await fetchFolderContent(rootFolderId, 0);
      if (libraryId) {
        console.log(`[LIBRARY-LOAD] Library Content ID: ${libraryId}`);
        setLibraryContentId(libraryId);
      }

      console.log(`[LIBRARY-LOAD] Loaded hierarchical content structure:`, {
        total: hierarchyContent.length,
        folders: hierarchyContent.filter((i) => i.isFolder).length,
        sections: hierarchyContent.filter((i) => i.isSection).length,
        items: hierarchyContent.map((i) => ({ id: i.id, isFolder: i.isFolder, isSection: i.isSection, label: i.label || i.caption })),
      });
      setLibraryContent(hierarchyContent);
    } catch (err) {
      console.error("Error loading library content:", err);
      setLibraryContent([]);
    } finally {
      setLoadingState((prev) => ({ ...prev, library: false }));
    }
  }, [procedureId]);

  const loadStandaloneReminders = useCallback(async () => {
    try {
      setLoadingState((prev) => ({ ...prev, standaloneReminders: true }));
      const response = await $.ajax({
        url: `/my-jeeva-api/fetch-milestone-reminders?treatmentId=${procedureId}`,
        type: "GET",
        headers: authHeaders(),
        dataType: "json",
      });
      setStandaloneReminders(response && response.result ? response.result : []);
    } catch (err) {
      console.error("Error loading stand-alone reminders:", err);
      setStandaloneReminders([]);
    } finally {
      setLoadingState((prev) => ({ ...prev, standaloneReminders: false }));
    }
  }, [procedureId]);

  /**
   * One button per reminder: a stand-alone reminder is only a title and a message, so there are
   * no child items to approve separately the way a journal's questions are.
   */
  const approveStandaloneReminder = async (reminderId) => {
    const approvalKey = `STANDALONE_REMINDER-${reminderId}-${selectedTranslateLanguage}`;
    try {
      setApprovingItems((prev) => ({ ...prev, [approvalKey]: true }));

      await $.ajax({
        url: `/my-jeeva-api/translation-approval/approve-item`,
        type: "POST",
        dataType: "json",
        contentType: "application/json",
        headers: authHeaders(),
        data: JSON.stringify({
          contentId: reminderId,
          itemId: reminderId,
          approvalType: "STANDALONE_REMINDER",
          languageCode: selectedTranslateLanguage,
        }),
      });

      await $.ajax({
        url: `/my-jeeva-api/translation-approval/mark-fully-approved`,
        type: "POST",
        dataType: "json",
        headers: authHeaders(),
        data: {
          contentId: reminderId,
          approvalType: "STANDALONE_REMINDER",
          languageCode: selectedTranslateLanguage,
        },
      });

      await loadApprovalStatus(reminderId, "STANDALONE_REMINDER");
      await loadProcedureApprovalStatuses();
      toast.dismiss();
      toast(<b><img src="assets/images/done.svg" style={{ paddingRight: '8px', height: '20px', width: '20px' }} content="" />&nbsp;&nbsp;Reminder translation approved</b>, {
        position: "top-center",
      });
    } catch (err) {
      console.error("[APPROVE-STANDALONE-REMINDER] Error:", err);
      toast.error("Failed to approve reminder");
    } finally {
      setApprovingItems((prev) => ({ ...prev, [approvalKey]: false }));
    }
  };

  const loadRiskRulesData = useCallback(async () => {
    try {
      setLoadingState((prev) => ({ ...prev, riskRules: true }));
      const riskResponse = await $.ajax({
        url: `/my-jeeva-api/get-risk-rules-procedure?procedureId=${procedureId}`,
        type: "GET",
        headers: authHeaders(),
        dataType: "json",
      });

      // Each group is one risk rule: the parent row (parentRuleId null) plus its component rows.
      const groupedRules = riskResponse && riskResponse.result ? riskResponse.result : {};
      const ruleGroups = Object.values(groupedRules).filter((group) => group && group.length > 0);

      const rulesWithMessages = await Promise.all(
        ruleGroups.map(async (group) => {
          const parent = group.find((r) => !r.parentRuleId) || group[0];
          const rule = { ...parent, components: [parent, ...group.filter((r) => r !== parent)] };
          try {
            const msgResponse = await $.ajax({
              url: "/my-jeeva-api/fetch-patient-message",
              type: "GET",
              headers: authHeaders(),
              dataType: "json",
              data: { parentRuleId: rule.id }
            });

            if (msgResponse && msgResponse.success && msgResponse.result) {
              return {
                ...rule,
                patientMessageId: msgResponse.result.id,
                patientMessage: msgResponse.result.customMessage
                  || resolvePreDefinedMessage(msgResponse.result.preDefinedMessage)
                  || ""
              };
            }
            return { ...rule, patientMessageId: null, patientMessage: "" };
          } catch (err) {
            console.error("Error loading patient message for rule:", err);
            return { ...rule, patientMessage: "" };
          }
        })
      );

      setRiskRules(rulesWithMessages);
    } catch (err) {
      console.error("Error loading risk rules:", err);
      setRiskRules([]);
    } finally {
      setLoadingState((prev) => ({ ...prev, riskRules: false }));
    }
  }, [procedureId]);

  const handleTranslationChange = (entityType, entityId, value, fieldType) => {
    const key = fieldType ? `${entityType}-${entityId}-${fieldType}` : `${entityType}-${entityId}`;
    setTranslationValues((prev) => ({ ...prev, [key]: value }));
  };

  const formatApprovalDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return { date: "", time: "" };
    try {
      const date = new Date(dateTimeStr);
      const dateStr = date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
      const timeStr = date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
      return { date: dateStr, time: timeStr };
    } catch (e) {
      return { date: "", time: "" };
    }
  };

  const toggleSection = (key) => {
    setExpandedSections((prev) => {
      const newState = {
        surveys: false,
        journals: false,
        library: false,
        riskrules: false,
        standaloneReminders: false,
        [key]: !prev[key],
      };

      if (!prev[key]) {
        if (key === "surveys") loadSurveys();
        else if (key === "journals") loadJournals();
        else if (key === "library") loadLibraryContent();
        else if (key === "riskrules") loadRiskRulesData();
        else if (key === "standaloneReminders") loadStandaloneReminders();
      }

      return newState;
    });
  };

  // Load approval status when surveys load
  useEffect(() => {
    if (surveys.length > 0 && expandedSections["surveys"] && selectedTranslateLanguage) {
      surveys.forEach((survey) => {
        loadApprovalStatus(survey.id, "SURVEY");
      });
    }
  }, [surveys, selectedTranslateLanguage]);

  // Load approval status when journals load
  useEffect(() => {
    if (journals.length > 0 && expandedSections["journals"] && selectedTranslateLanguage) {
      journals.forEach((journal) => {
        loadApprovalStatus(journal.id, "JOURNAL");
      });
    }
  }, [journals, selectedTranslateLanguage]);

  // Load approval status when stand-alone reminders load, so an already-approved one shows
  // as approved rather than offering the button again.
  useEffect(() => {
    if (standaloneReminders.length > 0 && expandedSections["standaloneReminders"] && selectedTranslateLanguage) {
      standaloneReminders.forEach((reminder) => {
        loadApprovalStatus(reminder.id, "STANDALONE_REMINDER");
      });
    }
  }, [standaloneReminders, selectedTranslateLanguage]);

  // Load approval status when risk rules load
  useEffect(() => {
    if (riskRules.length > 0 && expandedSections["riskrules"] && selectedTranslateLanguage) {
      riskRules.forEach((rule) => {
        if (rule.patientMessageId) {
          loadApprovalStatus(rule.patientMessageId, "PATIENTMESSAGE");
        }
      });
    }
  }, [riskRules, selectedTranslateLanguage]);

  // Load approval status when library content loads
  useEffect(() => {
    if (libraryContentId && selectedTranslateLanguage) {
      console.log(`[LIBRARY-LOAD-STATUS] Loading approval status for libraryContentId=${libraryContentId}`);
      loadApprovalStatus(libraryContentId, "LIBRARY");
    }
  }, [libraryContentId, selectedTranslateLanguage]);

  // Check if still loading
  if (availableLanguages === null) {
    return (
      <div style={{ display: "flex", justifyContent: "center", paddingTop: "24px" }}>
        <p style={{ fontSize: "14px", color: "#999" }}>⏳ Loading approvals...</p>
      </div>
    );
  }

  // The languages request itself failed - say so rather than claiming none are configured
  if (loadLanguagesFailed) {
    return (
      <div style={{ padding: "16px" }}>
        <div style={{ padding: "16px", background: "#f8d7da", border: "1px solid #dc3545", borderRadius: "4px", fontSize: "13px", color: "#721c24" }}>
          <div style={{ fontWeight: "600", marginBottom: "8px" }}>⚠️ Unable to Load Languages</div>
          <div>Could not load the languages configured for this treatment. Please refresh the page and try again.</div>
        </div>
      </div>
    );
  }

  // Check if any languages are configured
  if (availableLanguages.length === 0) {
    return (
      <div style={{ padding: "16px" }}>
        <div style={{ padding: "16px", background: "#fff3cd", border: "1px solid #ffc107", borderRadius: "4px", fontSize: "13px", color: "#856404" }}>
          <div style={{ fontWeight: "600", marginBottom: "8px" }}>⚠️ No Languages Configured</div>
          <div>No translation languages are enabled for this treatment. Please configure languages in the <strong>Language Selection Tab</strong> to enable translation approvals.</div>
        </div>
      </div>
    );
  }

  // A library section's text and, when it has one (image/video/audio sections carry only this),
  // its caption. Each asks for its own field explicitly - without a fieldType TranslatableItem
  // would show the caption in place of the text. The approve button goes on the first of the two.
  const renderLibrarySectionItems = (section, showApproveButton, isApproved) => {
    const hasText = !!(section.textContent && section.textContent.trim());
    const hasCaption = !!(section.caption && section.caption.trim());
    const approveProps = {
      showApproveButton: showApproveButton,
      onApprove: () => approveSingleItem(libraryContentId, section.id, "LIBRARY"),
      isApproveDisabled: isApproved,
    };
    return (
      <React.Fragment>
        {(hasText || !hasCaption) && (
          <TranslatableItem
            entityType="library"
            autoTranslateIfMissing={true}
            entityId={section.id}
            fieldType="textContent"
            label="Content"
            originalContent={section.textContent}
            selectedLanguage={selectedTranslateLanguage}
            onSave={saveTranslation}
            savingState={savingTranslations}
            translationValue={translationValues[`library-${section.id}-textContent`] || ""}
            onTranslationChange={handleTranslationChange}
            isRich={true}
            editorState={editorStates[`library-${section.id}-content`] || EditorState.createEmpty()}
            onEditorStateChange={(state) => setEditorStates((prev) => ({ ...prev, [`library-${section.id}-content`]: state }))}
            {...approveProps}
            procedureId={procedureId}
          />
        )}
        {hasCaption && (
          <TranslatableItem
            entityType="library"
            autoTranslateIfMissing={true}
            entityId={section.id}
            fieldType="caption"
            label="Caption"
            originalContent={section.caption}
            selectedLanguage={selectedTranslateLanguage}
            onSave={saveTranslation}
            savingState={savingTranslations}
            translationValue={translationValues[`library-${section.id}-caption`] || ""}
            onTranslationChange={handleTranslationChange}
            editorState={editorStates[`library-${section.id}-caption`] || EditorState.createEmpty()}
            onEditorStateChange={(state) => setEditorStates((prev) => ({ ...prev, [`library-${section.id}-caption`]: state }))}
            {...(hasText ? {} : approveProps)}
            procedureId={procedureId}
          />
        )}
      </React.Fragment>
    );
  };

  // The contents of a library folder at any depth, numbered from the parent ("2.1", "2.1.3", ...).
  const renderLibraryChildren = (children, parentNumber, approvedItemIds) =>
    children.map((child, childIdx) => {
      const isChildApproved = approvedItemIds.includes(child.id);
      const childNumber = `${parentNumber}.${childIdx + 1}`;

      return (
        <div key={child.id} style={{ marginLeft: "12px", marginTop: "12px", paddingBottom: "12px", borderBottom: "1px solid #f0f0f0" }}>
          {/* Nested Section */}
          {child.isSection && (
            <div>
              <h5 style={{ margin: "0 0 8px 0", color: "#333", fontSize: "12px", fontWeight: "600" }}>
                {childNumber}. {child.label || child.caption}
              </h5>
              {renderLibrarySectionItems(child, true, isChildApproved)}
            </div>
          )}

          {/* Nested Folder */}
          {child.isFolder && (
            <div>
              <h5 style={{ margin: "0 0 8px 0", color: "#667eea", fontSize: "12px", fontWeight: "600" }}>
                {childNumber}. 📁 {child.label || child.name}
              </h5>
              <TranslatableItem
                entityType="folder"
                autoTranslateIfMissing={true}
                entityId={child.id}
                fieldType="label"
                label="Folder Label"
                originalContent={child.label || child.name}
                selectedLanguage={selectedTranslateLanguage}
                onSave={saveTranslation}
                savingState={savingTranslations}
                translationValue={translationValues[`folder-${child.id}-label`] || ""}
                onTranslationChange={handleTranslationChange}
                editorState={editorStates[`folder-${child.id}-label`] || EditorState.createEmpty()}
                onEditorStateChange={(state) => setEditorStates((prev) => ({ ...prev, [`folder-${child.id}-label`]: state }))}
              />
              {child.description && (
                <TranslatableItem
                  entityType="folder"
                  autoTranslateIfMissing={true}
                  entityId={child.id}
                  fieldType="description"
                  label="Folder Description"
                  originalContent={child.description}
                  selectedLanguage={selectedTranslateLanguage}
                  onSave={saveTranslation}
                  savingState={savingTranslations}
                  translationValue={translationValues[`folder-${child.id}-description`] || ""}
                  onTranslationChange={handleTranslationChange}
                  editorState={editorStates[`folder-${child.id}-description`] || EditorState.createEmpty()}
                  onEditorStateChange={(state) => setEditorStates((prev) => ({ ...prev, [`folder-${child.id}-description`]: state }))}
                  showApproveButton={true}
                  onApprove={() => approveSingleItem(libraryContentId, child.id, "LIBRARY")}
                  isApproveDisabled={isChildApproved}
                />
              )}
              {child.children && child.children.length > 0 && renderLibraryChildren(child.children, childNumber, approvedItemIds)}
            </div>
          )}
        </div>
      );
    });

  return (
    <React.Fragment>
      {/* Loading Approvals Message - Only Loading */}
      {availableLanguages === null && (
        <div style={{ display: "flex", justifyContent: "center", paddingTop: "24px" }}>
          <p style={{ fontSize: "14px", color: "#999" }}>⏳ Loading approvals...</p>
        </div>
      )}

      {/* No Languages Configured Message - After Loading */}
      {availableLanguages !== null && availableLanguages.length === 0 && (
        <div style={{ padding: "16px" }}>
          <div style={{ padding: "16px", background: "#fff3cd", border: "1px solid #ffc107", borderRadius: "4px", fontSize: "13px", color: "#856404" }}>
            <div style={{ fontWeight: "600", marginBottom: "8px" }}>⚠️ No Languages Configured</div>
            <div>No translation languages are enabled for this treatment. Please configure languages in the <strong>Language Selection Tab</strong> to enable translation approvals.</div>
          </div>
        </div>
      )}

      {/* Main Content - Show Only If Languages Configured */}
      {availableLanguages !== null && availableLanguages.length > 0 && (
        <React.Fragment>
          {/* Language Selector */}
          <div style={{ marginBottom: "24px" }}>
            <label style={{ display: "block", marginBottom: "6px", fontWeight: "600", color: "#333", fontSize: "13px" }}>
              Select Language to Review & Approve
            </label>
            <select
              value={selectedTranslateLanguage}
              onChange={(e) => setSelectedTranslateLanguage(e.target.value)}
              style={{ padding: "8px 10px", border: "1px solid #e0e0e0", borderRadius: "0", fontSize: "13px", maxWidth: "280px", cursor: "pointer", background: "#fff" }}
            >
              <option value="">-- Select Language --</option>
              {availableLanguages.map((lang) => (
                <option key={lang.code} value={lang.code}>{lang.englishName} ({lang.displayName})</option>
              ))}
            </select>
          </div>

          {/* Approval Status Summary Modal */}
          {selectedTranslateLanguage && modalApprovalStatus && modalApprovalStatus.overallStatus && (
        <div style={{
          marginBottom: "24px",
          padding: "16px",
          background: (() => {
            const status = modalApprovalStatus.overallStatus;
            if (status === "NO_CONTENT") return "#f5f5f5";
            if (status === "APPROVED") return "#e8f5e9";
            return "#ffebee"; // PENDING
          })(),
          border: `2px solid ${(() => {
            const status = modalApprovalStatus.overallStatus;
            if (status === "NO_CONTENT") return "#999";
            if (status === "APPROVED") return "#4caf50";
            return "#ef5350"; // PENDING
          })()}`,
          borderRadius: "4px"
        }}>
          <div style={{ fontSize: "13px", fontWeight: "600", color: "#333", marginBottom: "12px" }}>
            {(() => {
              const status = modalApprovalStatus.overallStatus;
              if (status === "NO_CONTENT") return "No Content";
              if (status === "APPROVED") return "✓ All Content Approved";
              return "⚠ Pending Approvals";
            })()}
          </div>

          {/* Category Status Grid - Library, Journals, Risk Rules, Surveys, Stand-alone Reminders */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "12px" }}>
            {["Library", "Journals", "Risk Rules", "Surveys", "Stand-alone Reminders"].map((category) => {
              const categoryKey = category === "Surveys" ? "surveys"
                : category === "Journals" ? "journals"
                : category === "Library" ? "library"
                : category === "Stand-alone Reminders" ? "standaloneReminders"
                : "riskRules";
              const categoryData = modalApprovalStatus.categoryStatuses && modalApprovalStatus.categoryStatuses[categoryKey];

              if (!categoryData || !categoryData.exists) {
                return (
                  <div
                    key={category}
                    style={{
                      padding: "10px 12px",
                      background: "#f0f0f0",
                      border: "1px solid #ccc",
                      borderRadius: "3px",
                      fontSize: "12px",
                      color: "#999",
                      textAlign: "center"
                    }}
                  >
                    {category}
                    <div style={{ fontSize: "10px", marginTop: "4px" }}>—</div>
                  </div>
                );
              }

              const isApproved = categoryData.fullyApproved;

              return (
                <div
                  key={category}
                  style={{
                    padding: "10px 12px",
                    background: isApproved ? "#e8f5e9" : "#ffebee",
                    border: `1px solid ${isApproved ? "#81c784" : "#ef5350"}`,
                    borderRadius: "3px",
                    fontSize: "12px",
                    color: isApproved ? "#2e7d32" : "#c62828",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontWeight: "600", marginBottom: "4px" }}>
                    {isApproved ? "✓" : "⚠"} {category}
                  </div>
                  <div style={{ fontSize: "11px", opacity: 0.8 }}>
                    {isApproved ? "Approved" : "Pending"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Library Content Section */}
      <div style={{ marginBottom: "32px" }}>
          <div
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: expandedSections["library"] ? "#f0f0f0" : "#fff", cursor: "pointer", borderRadius: "0", marginBottom: "12px", border: "none", borderBottom: "1px solid #e0e0e0" }}
            onClick={() => toggleSection("library")}
          >
            <h3 style={{ margin: "0", color: "#333", fontSize: "14px", fontWeight: "600" }}>Library Content</h3>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              {/* Approve Library Content button - in header, visible only when expanded and not fully approved */}
              {(() => {
                const isLibraryApproved = modalApprovalStatus && modalApprovalStatus.categoryStatuses &&
                  modalApprovalStatus.categoryStatuses.library &&
                  modalApprovalStatus.categoryStatuses.library.fullyApproved;
                return expandedSections["library"] && libraryContent.length > 0 && !isLibraryApproved;
              })() && (
                <button
                  style={{
                    padding: "6px 14px",
                    borderRadius: "3px",
                    border: "1px solid",
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                    color: "#fff",
                    transition: "all 0.2s ease",
                    borderColor: (() => {
                      const key = `LIBRARY-${libraryContentId}-${selectedTranslateLanguage}`;
                      const isApproved = approvalStatus[key] && approvalStatus[key].approved === true;
                      return isApproved ? "#4caf50" : "#ff9800";
                    })(),
                    background: (() => {
                      const key = `LIBRARY-${libraryContentId}-${selectedTranslateLanguage}`;
                      const isApproved = approvalStatus[key] && approvalStatus[key].approved === true;
                      return isApproved ? "#4caf50" : "#ff9800";
                    })(),
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const libraryIds = getAllLibraryIds(libraryContent);

                    // Approve items SEQUENTIALLY (not in parallel) to ensure each updates the same entity
                    (async () => {
                      for (const id of libraryIds) {
                        const key = `LIBRARY-${libraryContentId}-${selectedTranslateLanguage}`;
                        if (approvalStatus[key] && !approvalStatus[key].isFullyApproved) {
                          await approveSingleItem(libraryContentId, id, "LIBRARY", true, false); // no toast; marked fully approved below
                        }
                      }
                      // After all items approved, mark fully approved
                      return true;
                    })().then(() => {
                      // After all items approved, mark fully approved
                      return $.ajax({
                        url: `/my-jeeva-api/translation-approval/mark-fully-approved`,
                        type: "POST",
                        dataType: "json",
                        headers: authHeaders(),
                        data: {
                          contentId: libraryContentId,
                          approvalType: "LIBRARY",
                          languageCode: selectedTranslateLanguage,
                        },
                      });
                    }).then(() => {
                      // GET /status - turns top button green + individual items green
                      return loadApprovalStatus(libraryContentId, "LIBRARY");
                    }).then(() => {
                      // GET /procedure/.../approval-status - updates modal
                      return loadProcedureApprovalStatuses();
                    }).then(() => {
                      // Show single toast at the end
                      toast.dismiss();
                      toast(<b><img src="assets/images/done.svg" style={{ paddingRight: '8px', height: '20px', width: '20px' }} content="" />&nbsp;&nbsp;All library content approved successfully</b>, {
                        position: "top-center",
                      });
                    });
                  }}
                  onMouseEnter={(e) => {
                    if (Object.keys(approvalStatus).some((key) => key.startsWith("LIBRARY-") && key.endsWith(`-${selectedTranslateLanguage}`) && !approvalStatus[key].isFullyApproved)) {
                      e.target.style.background = "#e68900";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (Object.keys(approvalStatus).some((key) => key.startsWith("LIBRARY-") && key.endsWith(`-${selectedTranslateLanguage}`) && !approvalStatus[key].isFullyApproved)) {
                      e.target.style.background = "#ff9800";
                    }
                  }}
                >
                  {(() => {
                    const key = `LIBRARY-${libraryContentId}-${selectedTranslateLanguage}`;
                    return (approvalStatus[key] && !approvalStatus[key].approved) ? "Approve All Library Content" : "✓ Library Approved";
                  })()}
                </button>
              )}
              <span style={{ fontSize: "16px", color: "#999" }}>{expandedSections["library"] ? "▼" : "▶"}</span>
            </div>
          </div>
          {expandedSections["library"] && (
            <div style={{ background: "#fff", borderRadius: "0", padding: "12px 16px", boxShadow: "none", border: "1px solid #e0e0e0", borderTop: "none" }}>
              {loadingState.library ? (
                <p style={{ color: "#999", padding: "12px", textAlign: "center", fontSize: "13px", margin: "0" }}>⏳ Loading library content...</p>
              ) : libraryContent.length === 0 ? (
                <p style={{ color: "#999", padding: "12px", textAlign: "center", fontSize: "13px", margin: "0" }}>No library content found</p>
              ) : (
                <div>
                  {libraryContent.map((item, itemIdx) => {
                    const libraryApprovalKey = `LIBRARY-${libraryContentId}-${selectedTranslateLanguage}`;
                    const itemApprovalStatus = approvalStatus[libraryApprovalKey] || {};
                    // Check if this specific item's id is in the approvedItemIds array
                    const isItemApproved = itemApprovalStatus.approvedItemIds && itemApprovalStatus.approvedItemIds.includes(item.id);
                    const itemNumber = itemIdx + 1;

                    return (
                      <div key={item.id}>
                        {/* TOP-LEVEL SECTION */}
                        {item.isSection && item.depth === 0 && (
                          <div style={{ marginBottom: "20px", paddingBottom: "12px", borderBottom: "1px solid #e0e0e0" }}>
                            <h4 style={{ margin: "0 0 12px 0", color: "#333", fontSize: "13px", fontWeight: "600" }}>
                              {itemNumber}. {item.label || item.caption}
                            </h4>
                            {renderLibrarySectionItems(
                              item,
                              (() => {
                                const isLibraryApproved = modalApprovalStatus && modalApprovalStatus.categoryStatuses &&
                                  modalApprovalStatus.categoryStatuses.library &&
                                  modalApprovalStatus.categoryStatuses.library.fullyApproved;
                                return !isLibraryApproved;
                              })(),
                              isItemApproved
                            )}
                          </div>
                        )}

                        {/* TOP-LEVEL FOLDER */}
                        {item.isFolder && item.depth === 0 && (
                          <div style={{ marginBottom: "20px", paddingBottom: "12px", borderLeft: "4px solid #667eea", paddingLeft: "12px" }}>
                            <h4 style={{ margin: "0 0 12px 0", color: "#667eea", fontSize: "13px", fontWeight: "600" }}>
                              {itemNumber}. 📁 {item.label || item.name}
                            </h4>

                            {/* Folder Label */}
                            <TranslatableItem
                              entityType="folder"
                              autoTranslateIfMissing={true}
                              entityId={item.id}
                              fieldType="label"
                              label="Folder Label"
                              originalContent={item.label || item.name}
                              selectedLanguage={selectedTranslateLanguage}
                              onSave={saveTranslation}
                              savingState={savingTranslations}
                              translationValue={translationValues[`folder-${item.id}-label`] || ""}
                              onTranslationChange={handleTranslationChange}
                              editorState={editorStates[`folder-${item.id}-label`] || EditorState.createEmpty()}
                              onEditorStateChange={(state) => setEditorStates((prev) => ({ ...prev, [`folder-${item.id}-label`]: state }))}
                            />

                            {/* Folder Description with Approve Button */}
                            {item.description && (
                              <TranslatableItem
                                entityType="folder"
                                autoTranslateIfMissing={true}
                                entityId={item.id}
                                fieldType="description"
                                label="Folder Description"
                                originalContent={item.description}
                                selectedLanguage={selectedTranslateLanguage}
                                onSave={saveTranslation}
                                savingState={savingTranslations}
                                translationValue={translationValues[`folder-${item.id}-description`] || ""}
                                onTranslationChange={handleTranslationChange}
                                editorState={editorStates[`folder-${item.id}-description`] || EditorState.createEmpty()}
                                onEditorStateChange={(state) => setEditorStates((prev) => ({ ...prev, [`folder-${item.id}-description`]: state }))}
                                showApproveButton={true}
                                onApprove={() => approveSingleItem(libraryContentId, item.id, "LIBRARY")}
                                isApproveDisabled={isItemApproved}
                              />
                            )}
                            {!item.description && (
                              <div style={{ marginTop: "8px", textAlign: "right" }}>
                                {itemApprovalStatus.isFullyApproved && (
                                  <span style={{ fontSize: "11px", color: "#4caf50", fontWeight: "600" }}>✓ Approved</span>
                                )}
                              </div>
                            )}

                            {/* Nested Children */}
                            {item.children && renderLibraryChildren(item.children, itemNumber, itemApprovalStatus.approvedItemIds || [])}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

      {/* Journals Section */}
      <div style={{ marginBottom: "32px" }}>
          <div
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: expandedSections["journals"] ? "#f0f0f0" : "#fff", cursor: "pointer", borderRadius: "0", marginBottom: "12px", border: "none", borderBottom: "1px solid #e0e0e0" }}
            onClick={() => toggleSection("journals")}
          >
            <h3 style={{ margin: "0", color: "#333", fontSize: "14px", fontWeight: "600" }}>Journals</h3>
            <span style={{ fontSize: "16px", color: "#999" }}>{expandedSections["journals"] ? "▼" : "▶"}</span>
          </div>
          {expandedSections["journals"] && (
            <div style={{ background: "#fff", borderRadius: "0", padding: "12px 16px", boxShadow: "none", border: "1px solid #e0e0e0", borderTop: "none" }}>
              {loadingState.journals ? (
                <p style={{ color: "#999", padding: "12px", textAlign: "center", fontSize: "13px", margin: "0" }}>⏳ Loading journals...</p>
              ) : journals.length === 0 ? (
                <p style={{ color: "#999", padding: "12px", textAlign: "center", fontSize: "13px", margin: "0" }}>No journals found</p>
              ) : (
                <div>{journals.map((journal, idx) => {
                  const approvalKey = `JOURNAL-${journal.id}-${selectedTranslateLanguage}`;
                  const isApproved = approvalStatus[approvalKey] && approvalStatus[approvalKey].isFullyApproved;
                  const allJournalsApproved = journals.every(j => {
                    const key = `JOURNAL-${j.id}-${selectedTranslateLanguage}`;
                    return approvalStatus[key] && approvalStatus[key].isFullyApproved;
                  });
                  return (
                    <div key={journal.id} style={{ marginBottom: "20px", paddingBottom: "16px", borderBottom: "1px solid #e0e0e0" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
                        <h4 style={{ margin: "0", color: "#333", fontSize: "13px", fontWeight: "600" }}>{idx + 1}. {journal.title}</h4>
                        {!isApproved && (
                        <button
                          style={{
                            padding: "4px 12px",
                            borderRadius: "3px",
                            border: `1px solid ${isApproved ? "#4caf50" : "#ff9800"}`,
                            fontSize: "12px",
                            fontWeight: "600",
                            cursor: "pointer",
                            background: isApproved ? "#4caf50" : "#ff9800",
                            color: "#fff",
                            transition: "all 0.2s ease",
                          }}
                          onClick={() => !isApproved && approveJournalItems(journal.id)}
                          onMouseEnter={(e) => {
                            if (!isApproved) {
                              e.target.style.background = "#e68900";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isApproved) {
                              e.target.style.background = "#ff9800";
                            }
                          }}
                        >
                          {isApproved ? "✓ Approve Journal" : "Approve Journal"}
                        </button>
                        )}
                      </div>

                      {/* Journal Title */}
                      {(() => {
                        const approvalStatus_journalTitle = approvalStatus[approvalKey] || {};
                        const isTitleApproved = journal.id && approvalStatus_journalTitle.approvedItemIds && approvalStatus_journalTitle.approvedItemIds.includes(journal.id);
                        return (
                          <TranslatableItem
                            entityType="journal"
                            entityId={journal.id}
                            fieldType="title"
                            label={`Journal Title: ${journal.title}`}
                            originalContent={journal.title}
                            selectedLanguage={selectedTranslateLanguage}
                            onSave={saveTranslation}
                            savingState={savingTranslations}
                            translationValue={translationValues[`journal-${journal.id}-title`] || ""}
                            onTranslationChange={handleTranslationChange}
                            isRich={false}
                            editorState={EditorState.createEmpty()}
                            onEditorStateChange={() => { }}
                            showApproveButton={(() => {
                              const isJournalApproved = modalApprovalStatus && modalApprovalStatus.categoryStatuses &&
                                modalApprovalStatus.categoryStatuses.journals &&
                                modalApprovalStatus.categoryStatuses.journals.fullyApproved;
                              return !isJournalApproved;
                            })()}
                            onApprove={() => approveSingleItem(journal.id, journal.id, "JOURNAL")}
                            isApproveDisabled={isTitleApproved}
                          />
                        );
                      })()}

                      {/* Journal Items */}
                      {journal.items && journal.items.length > 0 && (
                        <div style={{ marginLeft: "12px", marginTop: "12px" }}>
                          <h5 style={{ color: "#333", fontSize: "12px", fontWeight: "600", margin: "0 0 8px 0" }}>Questions, Options & Labels</h5>
                          {journal.items.map((item, itemIdx) => {
                            const approvalStatus_journalItem = approvalStatus[approvalKey] || {};
                            const isJournalItemApproved = approvalStatus_journalItem.approvedItemIds && approvalStatus_journalItem.approvedItemIds.includes(item.id);
                            return (
                              <React.Fragment key={item.id}>
                                {item.label && item.label.trim() !== "" && (
                                  <TranslatableItem
                                    entityType="journalItem"
                                    entityId={item.id}
                                    fieldType="label"
                                    label={`L: ${item.label}`}
                                    originalContent={item.label}
                                    selectedLanguage={selectedTranslateLanguage}
                                    onSave={saveTranslation}
                                    savingState={savingTranslations}
                                    translationValue={translationValues[`journalItem-${item.id}-label`] || ""}
                                    onTranslationChange={handleTranslationChange}
                                    isRich={false}
                                    editorState={EditorState.createEmpty()}
                                    onEditorStateChange={() => { }}
                                  />
                                )}
                                <TranslatableItem
                                  entityType="journalItem"
                                  entityId={item.id}
                                  fieldType="question"
                                  label={`Q: ${item.question}`}
                                  originalContent={item.question}
                                  selectedLanguage={selectedTranslateLanguage}
                                  onSave={saveTranslation}
                                  savingState={savingTranslations}
                                  translationValue={translationValues[`journalItem-${item.id}-question`] || ""}
                                  onTranslationChange={handleTranslationChange}
                                  isRich={false}
                                  editorState={EditorState.createEmpty()}
                                  onEditorStateChange={() => { }}
                                  showApproveButton={(() => {
                                    const isJournalApproved = modalApprovalStatus && modalApprovalStatus.categoryStatuses &&
                                      modalApprovalStatus.categoryStatuses.journals &&
                                      modalApprovalStatus.categoryStatuses.journals.fullyApproved;
                                    return !isJournalApproved && (!item.options || item.options.trim() === "");
                                  })()}
                                  onApprove={() => approveSingleItem(journal.id, item.id, "JOURNALITEM")}
                                  isApproveDisabled={isJournalItemApproved}
                                />
                                {item.options && item.options.trim() !== "" && (
                                  <TranslatableItem
                                    entityType="journalItem"
                                    entityId={item.id}
                                    fieldType="options"
                                    label={`A: ${item.options.substring(0, 50)}...`}
                                    originalContent={item.options}
                                    selectedLanguage={selectedTranslateLanguage}
                                    onSave={saveTranslation}
                                    savingState={savingTranslations}
                                    translationValue={translationValues[`journalItem-${item.id}-options`] || ""}
                                    onTranslationChange={handleTranslationChange}
                                    isRich={false}
                                    editorState={EditorState.createEmpty()}
                                    onEditorStateChange={() => { }}
                                    showApproveButton={(() => {
                                      const isJournalApproved = modalApprovalStatus && modalApprovalStatus.categoryStatuses &&
                                        modalApprovalStatus.categoryStatuses.journals &&
                                        modalApprovalStatus.categoryStatuses.journals.fullyApproved;
                                      return !isJournalApproved;
                                    })()}
                                    onApprove={() => approveSingleItem(journal.id, item.id, "JOURNALITEM")}
                                    isApproveDisabled={isJournalItemApproved}
                                  />
                                )}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      )}

                      {/* Journal Reminders */}
                      {journalReminders[journal.id] && journalReminders[journal.id].length > 0 && (
                        <div style={{ marginLeft: "12px", marginTop: "12px" }}>
                          <h5 style={{ color: "#333", fontSize: "12px", fontWeight: "600", margin: "0 0 8px 0" }}>Reminders</h5>
                          {journalReminders[journal.id].map((reminder, remIdx) => {
                            const approvalStatus_journalReminder = approvalStatus[approvalKey] || {};
                            const isReminderApproved = approvalStatus_journalReminder.approvedReminderIds && approvalStatus_journalReminder.approvedReminderIds.includes(reminder.id);
                            return (
                              <React.Fragment key={reminder.id}>
                                <TranslatableItem
                                  entityType="journalReminder"
                                  entityId={reminder.id}
                                  fieldType="title"
                                  label={`Reminder Title: ${reminder.title}`}
                                  originalContent={reminder.title}
                                  selectedLanguage={selectedTranslateLanguage}
                                  onSave={saveTranslation}
                                  savingState={savingTranslations}
                                  translationValue={translationValues[`journalReminder-${reminder.id}-title`] || ""}
                                  onTranslationChange={handleTranslationChange}
                                  isRich={false}
                                  editorState={EditorState.createEmpty()}
                                  onEditorStateChange={() => { }}
                                  showApproveButton={false}
                                  onApprove={() => approveSingleItem(journal.id, reminder.id, "JOURNALREMINDER")}
                                  isApproveDisabled={isReminderApproved}
                                />
                                {reminder.content && (
                                  <TranslatableItem
                                    entityType="journalReminder"
                                    entityId={reminder.id}
                                    fieldType="content"
                                    label="Reminder Content"
                                    originalContent={reminder.content}
                                    selectedLanguage={selectedTranslateLanguage}
                                    onSave={saveTranslation}
                                    savingState={savingTranslations}
                                    translationValue={translationValues[`journalReminder-${reminder.id}-content`] || ""}
                                    onTranslationChange={handleTranslationChange}
                                    isRich={false}
                                    editorState={EditorState.createEmpty()}
                                    onEditorStateChange={() => { }}
                                    showApproveButton={(() => {
                                      const isJournalApproved = modalApprovalStatus && modalApprovalStatus.categoryStatuses &&
                                        modalApprovalStatus.categoryStatuses.journals &&
                                        modalApprovalStatus.categoryStatuses.journals.fullyApproved;
                                      return !isJournalApproved;
                                    })()}
                                    onApprove={() => approveSingleItem(journal.id, reminder.id, "JOURNALREMINDER")}
                                    isApproveDisabled={isReminderApproved}
                                  />
                                )}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}</div>
              )}
            </div>
          )}
        </div>

      {/* Risk Rules Section */}
      <div style={{ marginBottom: "32px" }}>
          <div
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: expandedSections["riskrules"] ? "#f0f0f0" : "#fff", cursor: "pointer", borderRadius: "0", marginBottom: "12px", border: "none", borderBottom: "1px solid #e0e0e0" }}
            onClick={() => toggleSection("riskrules")}
          >
            <h3 style={{ margin: "0", color: "#333", fontSize: "14px", fontWeight: "600" }}>Risk Rules</h3>
            <span style={{ fontSize: "16px", color: "#999" }}>{expandedSections["riskrules"] ? "▼" : "▶"}</span>
          </div>
          {expandedSections["riskrules"] && (
            <div style={{ background: "#fff", borderRadius: "0", padding: "12px 16px", boxShadow: "none", border: "1px solid #e0e0e0", borderTop: "none" }}>
              {loadingState.riskRules ? (
                <p style={{ color: "#999", padding: "12px", textAlign: "center", fontSize: "13px", margin: "0" }}>⏳ Loading risk rules...</p>
              ) : riskRules.length === 0 ? (
                <p style={{ color: "#999", padding: "12px", textAlign: "center", fontSize: "13px", margin: "0" }}>No risk rules found</p>
              ) : (
                <div>{riskRules.map((rule, idx) => {
                  const approvalKey = `PATIENTMESSAGE-${rule.patientMessageId}-${selectedTranslateLanguage}`;
                  const approvalStatus_rule = approvalStatus[approvalKey] || {};
                  const isRuleApproved = approvalStatus_rule.approved === true || (approvalStatus_rule.isFullyApproved === true);
                  const isRiskRulesFullyApproved = modalApprovalStatus && modalApprovalStatus.categoryStatuses &&
                    modalApprovalStatus.categoryStatuses.riskRules &&
                    modalApprovalStatus.categoryStatuses.riskRules.fullyApproved;
                  return (
                    <div key={rule.id} style={{ marginBottom: "20px", paddingBottom: "16px", borderBottom: "1px solid #e0e0e0" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
                        <h4 style={{ margin: "0", color: "#333", fontSize: "13px", fontWeight: "600" }}>{idx + 1}. {rule.riskRuleName || rule.message || rule.riskMessage || "Risk Rule"}</h4>
                        {!isRiskRulesFullyApproved && isRiskRulesFullyApproved !== undefined && (
                        <button
                          style={{
                            padding: "4px 12px",
                            borderRadius: "3px",
                            border: `1px solid ${isRuleApproved ? "#4caf50" : "#ff9800"}`,
                            fontSize: "12px",
                            fontWeight: "600",
                            cursor: "pointer",
                            background: isRuleApproved ? "#4caf50" : "#ff9800",
                            color: "#fff",
                            transition: "all 0.2s ease",
                          }}
                          onClick={() => !isRuleApproved && approveSingleRiskRule(rule.id, rule.patientMessageId, rule.riskRuleName || "Risk Rule")}
                          onMouseEnter={(e) => {
                            if (!isRuleApproved) {
                              e.target.style.background = "#e68900";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isRuleApproved) {
                              e.target.style.background = "#ff9800";
                            }
                          }}
                          disabled={isRuleApproved}
                        >
                          {isRuleApproved ? "✓ Approved" : "Approve"}
                        </button>
                        )}
                      </div>
                      {rule.components && rule.components.length > 0 && (
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px", marginBottom: "12px" }}>
                          <thead>
                            <tr style={{ background: "#f7f7f7", textAlign: "left" }}>
                              <th style={{ padding: "6px 8px", border: "1px solid #e0e0e0" }}>Based On</th>
                              <th style={{ padding: "6px 8px", border: "1px solid #e0e0e0" }}>High Risk Rule</th>
                              <th style={{ padding: "6px 8px", border: "1px solid #e0e0e0" }}>Moderate Risk Rule</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rule.components.map((component) => (
                              <tr key={component.id}>
                                <td style={{ padding: "6px 8px", border: "1px solid #e0e0e0" }}>{component.type ? component.type.charAt(0).toUpperCase() + component.type.slice(1).toLowerCase().replace(/_/g, ' ') : ""}</td>
                                <td style={{ padding: "6px 8px", border: "1px solid #e0e0e0" }}>{riskRuleCondition(component, "High")}</td>
                                <td style={{ padding: "6px 8px", border: "1px solid #e0e0e0" }}>{riskRuleCondition(component, "Moderate")}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      {rule.patientMessage && (
                        <TranslatableItem
                          entityType="patientmessage"
                          entityId={rule.patientMessageId}
                          fieldType="message"
                          label="Patient Message"
                          originalContent={rule.patientMessage}
                          selectedLanguage={selectedTranslateLanguage}
                          onSave={saveTranslation}
                          savingState={savingTranslations}
                          translationValue={translationValues[`patientmessage-${rule.patientMessageId}-message`] || ""}
                          onTranslationChange={handleTranslationChange}
                          isRich={false}
                          editorState={EditorState.createEmpty()}
                          onEditorStateChange={() => { }}
                        />
                      )}
                    </div>
                  );
                })}</div>
              )}
            </div>
          )}
        </div>

      {/* Surveys Section */}
      <div style={{ marginBottom: "32px" }}>
          <div
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: expandedSections["surveys"] ? "#f0f0f0" : "#fff", cursor: "pointer", borderRadius: "0", marginBottom: "12px", border: "none", borderBottom: "1px solid #e0e0e0" }}
            onClick={() => toggleSection("surveys")}
          >
            <h3 style={{ margin: "0", color: "#333", fontSize: "14px", fontWeight: "600" }}>Surveys</h3>
            <span style={{ fontSize: "16px", color: "#999" }}>{expandedSections["surveys"] ? "▼" : "▶"}</span>
          </div>
          {expandedSections["surveys"] && (
            <div style={{ background: "#fff", borderRadius: "0", padding: "12px 16px", boxShadow: "none", border: "1px solid #e0e0e0", borderTop: "none" }}>
              {loadingState.surveys ? (
                <p style={{ color: "#999", padding: "12px", textAlign: "center", fontSize: "13px", margin: "0" }}>⏳ Loading surveys...</p>
              ) : surveys.length === 0 ? (
                <p style={{ color: "#999", padding: "12px", textAlign: "center", fontSize: "13px", margin: "0" }}>No surveys found</p>
              ) : (
                <div>{surveys.map((survey, idx) => {
                  const approvalKey = `SURVEY-${survey.id}-${selectedTranslateLanguage}`;
                  const isApproved = approvalStatus[approvalKey] && approvalStatus[approvalKey].isFullyApproved;
                  const allSurveysApproved = surveys.every(s => {
                    const key = `SURVEY-${s.id}-${selectedTranslateLanguage}`;
                    return approvalStatus[key] && approvalStatus[key].isFullyApproved;
                  });
                  return (
                    <div key={survey.id} style={{ marginBottom: "20px", paddingBottom: "16px", borderBottom: "1px solid #e0e0e0" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
                        <h4 style={{ margin: "0", color: "#333", fontSize: "13px", fontWeight: "600" }}>{idx + 1}. {survey.title}</h4>
                        {!isApproved && (
                        <button
                          style={{
                            padding: "4px 12px",
                            borderRadius: "3px",
                            border: `1px solid ${isApproved ? "#4caf50" : "#ff9800"}`,
                            fontSize: "12px",
                            fontWeight: "600",
                            cursor: "pointer",
                            background: isApproved ? "#4caf50" : "#ff9800",
                            color: "#fff",
                            transition: "all 0.2s ease",
                          }}
                          onClick={() => !isApproved && approveSurveyItems(survey.id)}
                          onMouseEnter={(e) => {
                            if (!isApproved) {
                              e.target.style.background = "#e68900";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isApproved) {
                              e.target.style.background = "#ff9800";
                            }
                          }}
                        >
                          {isApproved ? "✓ Approve Survey" : "Approve Survey"}
                        </button>
                        )}
                      </div>

                      {/* Survey Title */}
                      {(() => {
                        const approvalStatus_surveyTitle = approvalStatus[approvalKey] || {};
                        const isTitleApproved = survey.id && approvalStatus_surveyTitle.approvedItemIds && approvalStatus_surveyTitle.approvedItemIds.includes(survey.id);
                        return (
                          <TranslatableItem
                            entityType="survey"
                            entityId={survey.id}
                            fieldType="title"
                            label={`Survey Title: ${survey.title}`}
                            originalContent={survey.title}
                            selectedLanguage={selectedTranslateLanguage}
                            onSave={saveTranslation}
                            savingState={savingTranslations}
                            translationValue={translationValues[`survey-${survey.id}-title`] || ""}
                            onTranslationChange={handleTranslationChange}
                            isRich={false}
                            editorState={EditorState.createEmpty()}
                            onEditorStateChange={() => { }}
                            showApproveButton={(() => {
                              const isSurveyApproved = modalApprovalStatus && modalApprovalStatus.categoryStatuses &&
                                modalApprovalStatus.categoryStatuses.surveys &&
                                modalApprovalStatus.categoryStatuses.surveys.fullyApproved;
                              return !isSurveyApproved;
                            })()}
                            onApprove={() => approveSingleItem(survey.id, survey.id, "SURVEY")}
                            isApproveDisabled={isTitleApproved}
                          />
                        );
                      })()}

                      {/* Survey Items */}
                      {survey.items && survey.items.length > 0 && (
                        <div style={{ marginLeft: "12px", marginTop: "12px" }}>
                          <h5 style={{ color: "#333", fontSize: "12px", fontWeight: "600", margin: "0 0 8px 0" }}>Questions & Options</h5>
                          {survey.items.map((item, itemIdx) => {
                            const approvalStatus_surveyItem = approvalStatus[approvalKey] || {};
                            const isItemApproved = approvalStatus_surveyItem.approvedItemIds && approvalStatus_surveyItem.approvedItemIds.includes(item.id);
                            return (
                              <React.Fragment key={item.id}>
                                <TranslatableItem
                                  entityType="surveyItem"
                                  entityId={item.id}
                                  fieldType="question"
                                  label={`Q: ${item.question}`}
                                  originalContent={item.question}
                                  selectedLanguage={selectedTranslateLanguage}
                                  onSave={saveTranslation}
                                  savingState={savingTranslations}
                                  translationValue={translationValues[`surveyItem-${item.id}-question`] || ""}
                                  onTranslationChange={handleTranslationChange}
                                  isRich={false}
                                  editorState={EditorState.createEmpty()}
                                  onEditorStateChange={() => { }}
                                  showApproveButton={(() => {
                                    const isSurveyApproved = modalApprovalStatus && modalApprovalStatus.categoryStatuses &&
                                      modalApprovalStatus.categoryStatuses.surveys &&
                                      modalApprovalStatus.categoryStatuses.surveys.fullyApproved;
                                    return !isSurveyApproved && (!item.options || item.options.trim() === "");
                                  })()}
                                  onApprove={() => approveSingleItem(survey.id, item.id, "SURVEYITEM")}
                                  isApproveDisabled={isItemApproved}
                                />
                                {item.options && item.options.trim() !== "" && (
                                  <TranslatableItem
                                    entityType="surveyItem"
                                    entityId={item.id}
                                    fieldType="options"
                                    label={`A: ${item.options.substring(0, 50)}...`}
                                    originalContent={item.options}
                                    selectedLanguage={selectedTranslateLanguage}
                                    onSave={saveTranslation}
                                    savingState={savingTranslations}
                                    translationValue={translationValues[`surveyItem-${item.id}-options`] || ""}
                                    onTranslationChange={handleTranslationChange}
                                    isRich={false}
                                    editorState={EditorState.createEmpty()}
                                    onEditorStateChange={() => { }}
                                    showApproveButton={(() => {
                                      const isSurveyApproved = modalApprovalStatus && modalApprovalStatus.categoryStatuses &&
                                        modalApprovalStatus.categoryStatuses.surveys &&
                                        modalApprovalStatus.categoryStatuses.surveys.fullyApproved;
                                      return !isSurveyApproved;
                                    })()}
                                    onApprove={() => approveSingleItem(survey.id, item.id, "SURVEYITEM")}
                                    isApproveDisabled={isItemApproved}
                                  />
                                )}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      )}

                      {/* Survey Reminders */}
                      {surveyReminders[survey.id] && surveyReminders[survey.id].length > 0 && (
                        <div style={{ marginLeft: "12px", marginTop: "12px" }}>
                          <h5 style={{ color: "#333", fontSize: "12px", fontWeight: "600", margin: "0 0 8px 0" }}>Reminders</h5>
                          {surveyReminders[survey.id].map((reminder, remIdx) => {
                            const approvalStatus_surveyReminder = approvalStatus[approvalKey] || {};
                            const isReminderApproved = approvalStatus_surveyReminder.approvedReminderIds && approvalStatus_surveyReminder.approvedReminderIds.includes(reminder.id);
                            return (
                              <React.Fragment key={reminder.id}>
                                <TranslatableItem
                                  entityType="surveyReminder"
                                  entityId={reminder.id}
                                  fieldType="title"
                                  label={`Reminder Title: ${reminder.title}`}
                                  originalContent={reminder.title}
                                  selectedLanguage={selectedTranslateLanguage}
                                  onSave={saveTranslation}
                                  savingState={savingTranslations}
                                  translationValue={translationValues[`surveyReminder-${reminder.id}-title`] || ""}
                                  onTranslationChange={handleTranslationChange}
                                  isRich={false}
                                  editorState={EditorState.createEmpty()}
                                  onEditorStateChange={() => { }}
                                  showApproveButton={false}
                                  onApprove={() => approveSingleItem(survey.id, reminder.id, "SURVEYREMINDER")}
                                  isApproveDisabled={isReminderApproved}
                                />
                                {reminder.content && (
                                  <TranslatableItem
                                    entityType="surveyReminder"
                                    entityId={reminder.id}
                                    fieldType="content"
                                    label="Reminder Content"
                                    originalContent={reminder.content}
                                    selectedLanguage={selectedTranslateLanguage}
                                    onSave={saveTranslation}
                                    savingState={savingTranslations}
                                    translationValue={translationValues[`surveyReminder-${reminder.id}-content`] || ""}
                                    onTranslationChange={handleTranslationChange}
                                    isRich={false}
                                    editorState={EditorState.createEmpty()}
                                    onEditorStateChange={() => { }}
                                    showApproveButton={(() => {
                                      const isSurveyApproved = modalApprovalStatus && modalApprovalStatus.categoryStatuses &&
                                        modalApprovalStatus.categoryStatuses.surveys &&
                                        modalApprovalStatus.categoryStatuses.surveys.fullyApproved;
                                      return !isSurveyApproved;
                                    })()}
                                    onApprove={() => approveSingleItem(survey.id, reminder.id, "SURVEYREMINDER")}
                                    isApproveDisabled={isReminderApproved}
                                  />
                                )}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}</div>
              )}
            </div>
          )}
        </div>

      {/* Stand-alone Reminders Section */}
      <div style={{ marginBottom: "32px" }}>
          <div
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: expandedSections["standaloneReminders"] ? "#f0f0f0" : "#fff", cursor: "pointer", borderRadius: "0", marginBottom: "12px", border: "none", borderBottom: "1px solid #e0e0e0" }}
            onClick={() => toggleSection("standaloneReminders")}
          >
            <h3 style={{ margin: "0", color: "#333", fontSize: "14px", fontWeight: "600" }}>Stand-alone Reminders</h3>
            <span style={{ fontSize: "16px", color: "#999" }}>{expandedSections["standaloneReminders"] ? "▼" : "▶"}</span>
          </div>
          {expandedSections["standaloneReminders"] && (
            <div style={{ background: "#fff", borderRadius: "0", padding: "12px 16px", boxShadow: "none", border: "1px solid #e0e0e0", borderTop: "none" }}>
              {loadingState.standaloneReminders ? (
                <p style={{ color: "#999", padding: "12px", textAlign: "center", fontSize: "13px", margin: "0" }}>⏳ Loading stand-alone reminders...</p>
              ) : standaloneReminders.length === 0 ? (
                <p style={{ color: "#999", padding: "12px", textAlign: "center", fontSize: "13px", margin: "0" }}>No stand-alone reminders found</p>
              ) : (
                <div>{standaloneReminders.map((reminder, idx) => {
                  const approvalKey = `STANDALONE_REMINDER-${reminder.id}-${selectedTranslateLanguage}`;
                  const isApproved = approvalStatus[approvalKey] && approvalStatus[approvalKey].isFullyApproved;
                  return (
                    <div key={reminder.id} style={{ marginBottom: "20px", paddingBottom: "16px", borderBottom: "1px solid #e0e0e0" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
                        <h4 style={{ margin: "0", color: "#333", fontSize: "13px", fontWeight: "600" }}>
                          {idx + 1}. {reminder.title}
                          <span style={{ color: "#999", fontWeight: "400", marginLeft: "8px" }}>
                            ({reminder.milestoneName})
                          </span>
                        </h4>
                        {!isApproved && (
                          <button
                            style={{
                              padding: "4px 12px",
                              borderRadius: "3px",
                              border: "1px solid #ff9800",
                              fontSize: "12px",
                              fontWeight: "600",
                              cursor: "pointer",
                              background: "#ff9800",
                              color: "#fff",
                              transition: "all 0.2s ease",
                            }}
                            onClick={() => approveStandaloneReminder(reminder.id)}
                            onMouseEnter={(e) => { e.target.style.background = "#e68900"; }}
                            onMouseLeave={(e) => { e.target.style.background = "#ff9800"; }}
                          >
                            Approve Reminder
                          </button>
                        )}
                        {isApproved && (
                          <span style={{ fontSize: "12px", fontWeight: "600", color: "#4caf50" }}>✓ Approved</span>
                        )}
                      </div>

                      <TranslatableItem
                        entityType="milestoneReminder"
                        entityId={reminder.id}
                        fieldType="title"
                        label={`Reminder title: ${reminder.title}`}
                        originalContent={reminder.title}
                        selectedLanguage={selectedTranslateLanguage}
                        onSave={saveTranslation}
                        savingState={savingTranslations}
                        translationValue={translationValues[`milestoneReminder-${reminder.id}-title`] || ""}
                        onTranslationChange={handleTranslationChange}
                        isRich={false}
                        editorState={EditorState.createEmpty()}
                        onEditorStateChange={() => { }}
                        procedureId={procedureId}
                      />

                      <TranslatableItem
                        entityType="milestoneReminder"
                        entityId={reminder.id}
                        fieldType="content"
                        label={`Reminder message: ${reminder.content}`}
                        originalContent={reminder.content}
                        selectedLanguage={selectedTranslateLanguage}
                        onSave={saveTranslation}
                        savingState={savingTranslations}
                        translationValue={translationValues[`milestoneReminder-${reminder.id}-content`] || ""}
                        onTranslationChange={handleTranslationChange}
                        isRich={false}
                        editorState={EditorState.createEmpty()}
                        onEditorStateChange={() => { }}
                        procedureId={procedureId}
                      />
                    </div>
                  );
                })}</div>
              )}
            </div>
          )}
        </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}



// WEBPACK FOOTER //
// src/my-jeeva/projectmgmt/TranslationApprovalsTab.js